import React, { useState, useEffect } from 'react';
import { Mail, Phone, MapPin, Clock, MessageSquare, ArrowRight } from 'lucide-react';
import { toast } from 'sonner';
import SEO from '../components/SEO';
import { submitFormWithGeolocation, getGeoLocation } from '../services/apiService';

const Contact = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    countryCode: '+1',
    message: ''
  });
  const [loading, setLoading] = useState(false);

  // Fetch geolocation on component mount
  useEffect(() => {
    const fetchGeo = async () => {
      const data = await getGeoLocation();
      if (data?.isSuccess) {
        console.log('User location:', data.data.countryName);
      }
    };
    fetchGeo();
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    
    const result = await submitFormWithGeolocation(formData);
    
    if (!result.success) {
      toast.error(result.error);
      setLoading(false);
    }
    // If success, user will be redirected automatically
  };

  return (
    <div className="min-h-screen bg-white">
      <SEO 
        title="Contact File In Canada - Canadian Trademark Experts"
        description="Contact File In Canada for expert Canadian trademark registration help. Our CIPO specialists are ready to protect your brand. Fast response guaranteed."
        keywords="contact File In Canada, Canadian trademark help, CIPO services, trademark consultation Canada, brand protection contact"
      />
      
      {/* Hero Section */}
      <section className="relative pt-32 pb-24 bg-gradient-to-br from-slate-900 via-teal-900 to-slate-800">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-4xl mx-auto">
            <div className="inline-flex items-center bg-teal-600/30 backdrop-blur-sm rounded-full px-6 py-3 mb-8 border border-teal-400/30">
              <MessageSquare className="w-5 h-5 text-emerald-400 mr-2" />
              <span className="text-white font-bold">24-Hour Response Guarantee</span>
            </div>
            <h1 className="text-5xl md:text-6xl font-bold text-white mb-8 leading-tight">
              Let's Protect<br />
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-emerald-400 to-teal-400">Your Brand</span>
            </h1>
            <p className="text-2xl text-slate-200 leading-relaxed font-light">
              Our Canadian trademark experts are standing by to answer your questions and start your CIPO registration
            </p>
          </div>
        </div>
      </section>

      {/* Contact Form & Info */}
      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-start">
            {/* Contact Form */}
            <div className="bg-white rounded-3xl shadow-2xl p-10 border-4 border-teal-600/20">
              <h2 className="text-4xl font-bold text-slate-900 mb-3">Get Started Today</h2>
              <p className="text-lg text-slate-600 leading-relaxed mb-8">Fill out the form and we'll respond within 24 hours</p>
              
              <form onSubmit={handleSubmit} className="space-y-6">
                <div>
                  <label className="block text-sm font-bold text-slate-700 mb-2">
                    Full Name *
                  </label>
                  <input
                    type="text"
                    required
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    className="w-full px-4 py-4 border-2 border-slate-200 rounded-xl focus:ring-2 focus:ring-teal-600 focus:border-teal-600 transition-all text-lg"
                    placeholder="John Smith"
                  />
                </div>

                <div>
                  <label className="block text-sm font-bold text-slate-700 mb-2">
                    Email Address *
                  </label>
                  <input
                    type="email"
                    required
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    className="w-full px-4 py-4 border-2 border-slate-200 rounded-xl focus:ring-2 focus:ring-teal-600 focus:border-teal-600 transition-all text-lg"
                    placeholder="john@example.com"
                  />
                </div>

                <div>
                  <label className="block text-sm font-bold text-slate-700 mb-2">
                    Contact Number *
                  </label>
                  <div className="flex gap-3">
                    <select
                      value={formData.countryCode}
                      onChange={(e) => setFormData({ ...formData, countryCode: e.target.value })}
                      className="w-auto px-3 py-4 border-2 border-slate-200 rounded-xl focus:ring-2 focus:ring-teal-600 focus:border-teal-600 transition-all text-sm"
                    >
                      <option value="+1">+1 US/Canada</option>
                      <option value="+44">+44 UK</option>
                      <option value="+91">+91 India</option>
                      <option value="+86">+86 China</option>
                      <option value="+81">+81 Japan</option>
                      <option value="+82">+82 South Korea</option>
                      <option value="+61">+61 Australia</option>
                      <option value="+49">+49 Germany</option>
                      <option value="+33">+33 France</option>
                      <option value="+39">+39 Italy</option>
                      <option value="+34">+34 Spain</option>
                      <option value="+7">+7 Russia</option>
                      <option value="+55">+55 Brazil</option>
                      <option value="+52">+52 Mexico</option>
                      <option value="+27">+27 South Africa</option>
                      <option value="+971">+971 UAE</option>
                      <option value="+65">+65 Singapore</option>
                      <option value="+31">+31 Netherlands</option>
                      <option value="+46">+46 Sweden</option>
                    </select>
                    <input
                      type="tel"
                      required
                      value={formData.phone}
                      onChange={(e) => {
                        const value = e.target.value.replace(/\D/g, '');
                        if (value.length <= 10) {
                          setFormData({ ...formData, phone: value });
                        }
                      }}
                      className="flex-1 px-4 py-4 border-2 border-slate-200 rounded-xl focus:ring-2 focus:ring-teal-600 focus:border-teal-600 transition-all text-lg"
                      placeholder="8884541849"
                      maxLength="10"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-bold text-slate-700 mb-2">
                    Message *
                  </label>
                  <textarea
                    required
                    value={formData.message}
                    onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                    rows="5"
                    className="w-full px-4 py-4 border-2 border-slate-200 rounded-xl focus:ring-2 focus:ring-teal-600 focus:border-teal-600 transition-all resize-none text-lg"
                    placeholder="Tell us about your trademark needs..."
                  />
                </div>

                <button
                  type="submit"
                  disabled={loading}
                  className="w-full bg-teal-600 text-white font-bold py-5 px-6 rounded-xl hover:bg-yellow-400 hover:text-slate-900 transform hover:scale-105 transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed shadow-2xl text-xl flex items-center justify-center"
                >
                  {loading ? 'Sending...' : (
                    <>
                      <span>Send Message</span>
                      <ArrowRight className="w-6 h-6 ml-2" />
                    </>
                  )}
                </button>
              </form>
            </div>

            {/* Contact Information */}
            <div className="space-y-8">
              <div className="bg-white rounded-3xl shadow-2xl p-10 border-4 border-slate-200">
                <h2 className="text-4xl font-bold text-slate-900 mb-8">Contact Information</h2>
                
                <div className="space-y-6">
                  <div className="flex items-start space-x-5 p-6 bg-white rounded-2xl border-2 border-teal-600/20">
                    <div className="bg-teal-600 p-4 rounded-xl flex-shrink-0">
                      <MapPin className="w-7 h-7 text-white" />
                    </div>
                    <div>
                      <h3 className="font-bold text-slate-900 mb-2 text-lg">Office Location</h3>
                      <p className="text-slate-600 font-semibold">2300 Yonge St, Toronto, ON M4P 1E4, Canada</p>
                    </div>
                  </div>

                  <div className="flex items-start space-x-5 p-6 bg-white rounded-2xl border-2 border-teal-600/20">
                    <div className="bg-teal-600 p-4 rounded-xl flex-shrink-0">
                      <Phone className="w-7 h-7 text-white" />
                    </div>
                    <div>
                      <h3 className="font-bold text-slate-900 mb-2 text-lg">Phone</h3>
                      <p className="text-slate-600 font-semibold">+1 (888) 752 5038</p>
                      <p className="text-sm text-slate-500 mt-1 font-medium">Mon-Fri, 9:00 AM - 6:00 PM EST</p>
                    </div>
                  </div>

                  <div className="flex items-start space-x-5 p-6 bg-white rounded-2xl border-2 border-teal-600/20">
                    <div className="bg-teal-600 p-4 rounded-xl flex-shrink-0">
                      <Mail className="w-7 h-7 text-white" />
                    </div>
                    <div>
                      <h3 className="font-bold text-slate-900 mb-2 text-lg">Email</h3>
                      <p className="text-slate-600 font-semibold">info@fileincanada.com</p>
                      <p className="text-sm text-slate-500 mt-1 font-medium">24-hour response guarantee</p>
                    </div>
                  </div>

                  <div className="flex items-start space-x-5 p-6 bg-white rounded-2xl border-2 border-teal-600/20">
                    <div className="bg-teal-600 p-4 rounded-xl flex-shrink-0">
                      <Clock className="w-7 h-7 text-white" />
                    </div>
                    <div>
                      <h3 className="font-bold text-slate-900 mb-2 text-lg">Business Hours</h3>
                      <p className="text-slate-600 font-semibold text-sm">Monday - Friday: 9:00 AM - 6:00 PM EST</p>
                      <p className="text-slate-600 font-semibold text-sm">Saturday: 10:00 AM - 2:00 PM EST</p>
                      <p className="text-slate-600 font-semibold text-sm">Sunday: Closed</p>
                    </div>
                  </div>
                </div>
              </div>

              <div className="bg-gradient-to-br from-slate-900 via-teal-900 to-slate-900 rounded-3xl p-10 text-white shadow-2xl border-4 border-teal-600/30">
                <h3 className="text-3xl font-bold mb-4">Need Immediate Help?</h3>
                <p className="text-xl text-slate-200 leading-relaxed mb-8 leading-relaxed">
                  Our trademark specialists are ready to answer your questions and guide you through the CIPO registration process.
                </p>
                <div className="flex flex-col sm:flex-row gap-4">
                  <a
                    href="tel:+18887525038"
                    className="inline-flex items-center justify-center bg-white0 text-slate-900 font-bold px-8 py-4 rounded-xl hover:bg-yellow-400 transition-all duration-200 shadow-xl"
                  >
                    <Phone className="w-5 h-5 mr-2" />
                    Call Us Now
                  </a>
                  <button
                    className="js-chat inline-flex items-center justify-center border-3 border-white text-white font-bold px-8 py-4 rounded-xl hover:bg-yellow-400 hover:text-slate-900 transition-all duration-200"
                  >
                    <MessageSquare className="w-5 h-5 mr-2" />
                    Chat with Expert
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Contact;